// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  AlertCircle, AlertTriangle, ArrowRight, Clock, FileCheck2, FileText,
  Inbox, MessageSquare, Pill, Truck, RefreshCcw, XCircle, Activity, ClipboardList,
} from 'lucide-react'
import * as api from '../../api/client'
import SpecialtyBadge, { SpecialtyFlag } from '../../components/SpecialtyBadge'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, relativeTime } from '../../components/workflow/StatePills'
import { useAuth } from '../../auth/AuthContext'
import { metricRxLink } from '../../lib/metricLink'

interface Props { onScreenEnter: () => void }

export default function OwnerHome({ onScreenEnter }: Props) {
  const navigate = useNavigate()
  const { me } = useAuth()
  const [data, setData] = useState<api.OwnerSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // ASP-32 / D-HOME-1: per-pharmacy home layout (builder-managed 'home' surface).
  // No saved layout (source='default') => defaults below keep the built-in order with
  // every section visible, so unconfigured pharmacies render exactly as before.
  const [homeLayout, setHomeLayout] = useState<api.RxViewCard[] | null>(null)
  // ASP-72: headline-count breakdown panel open/closed.
  const [showAttention, setShowAttention] = useState(false)
  const [metricResults, setMetricResults] = useState<Record<string, api.HomeMetricResult>>({})
  // ASP-53: live counts for custom-view home tiles, keyed by card_key.
  const [viewTiles, setViewTiles] = useState<Record<string, { count: number; name: string; ok: boolean }>>({})

  const [refreshedAt, setRefreshedAt] = useState<Date>(new Date())

  // D-CRX-23: specialty badges on home card lists. Hooks live in the top block (P0 lesson:
  // never below an early return). Fail-quiet — module-gated, BioAccess home renders as today.
  const [spFlags, setSpFlags] = useState<Record<string, SpecialtyFlag>>({})
  useEffect(() => {
    const a = data?.action_lane
    if (!a) return
    const ids = Array.from(new Set([
      ...(a.recent_comments ?? []).map((c: { patient_id: string }) => c.patient_id),
      ...(a.old_holds ?? []).map((h: { patient_id: string }) => h.patient_id),
      ...(a.aging_precheck?.items ?? []).map((x: { patient_id: string }) => x.patient_id),
    ].filter(Boolean)))
    if (ids.length === 0) { setSpFlags({}); return }
    api.getCareRxSpecialtyFlags(ids.slice(0, 200)).then(r => setSpFlags(r.flags)).catch(() => setSpFlags({}))
    // PERF: `data` is an object reference — same defect as the patients list and
    // L-077. Left keyed on `data` here because OwnerHome refetches `data` on a
    // timer rather than per render, so the amplification is bounded; converting it
    // needs a stable key derived from the id set and is a separate change I will
    // not bundle with a hotfix.
  }, [data])

  // WP-5 Sprint 14: auto-refresh every 60s so the dashboard stays current
  // as PioneerRx events flow in. Pharmacy staff want at-a-glance metrics
  // without having to manually reload.
  // BADGE-SAFE (P0 2026-08-06 postmortem): single-flight — a slow owner-summary must never
  // have the next interval tick stack a second request on top of it (the outage log showed
  // GetOwnerSummary dying alongside the view-count pile-up).
  const refreshInFlight = useRef(false)
  const refresh = (silent = false) => {
    if (silent && refreshInFlight.current) return
    refreshInFlight.current = true
    if (!silent) setLoading(true)
    setError(null)
    api.getOwnerSummary()
      .then(d => { setData(d); setRefreshedAt(new Date()) })
      .catch(e => setError(friendlyError(e)))
      .finally(() => { refreshInFlight.current = false; setLoading(false) })
  }

  useEffect(() => {
    onScreenEnter()
    refresh()
    // Layout is fetched once; failures fall back to the default layout (fail-open on render).
    api.getRxViewLayout(undefined, 'home')
      .then(l => {
        if (l.source !== 'custom') return
        setHomeLayout(l.cards)
        // D-HOME-2 part B: resolve configurable metric cards. Only fires when the saved
        // layout actually contains one — pharmacies without metric cards (and pharmacies
        // with no saved layout at all) never hit this endpoint.
        if (l.cards.some(c => c.kind === 'home_metric' && c.visible))
          api.getHomeMetrics()
            .then(m => setMetricResults(Object.fromEntries(m.cards.map(c => [c.card_key, c]))))
            .catch(() => { /* metric cards render as unavailable */ })
        // ASP-53: resolve custom-view tiles — live total per saved view.
        // D-BADGE-1 (Mario 2026-08-07): ONE batched /views/counts call serves every tile —
        // the per-tile count_only calls (BADGE-SAFE interim) still fired independently per
        // view. Per-tile fallback remains for: a view missing from the batch response
        // (RBAC/hidden), a failed entry (null), an UNTITLED card (the tile label needs the
        // view_name only executeView returns), or an older backend without the endpoint.
        const tileCards = l.cards.filter(c => c.kind === 'custom_view' && c.visible && (c as any).config?.view?.view_id)
        if (tileCards.length > 0) {
          const tileFallback = (c: api.RxViewCard, vid: string) =>
            api.executeView(vid, { limit: 1, countOnly: true })
              .then(r => setViewTiles(prev => ({ ...prev, [c.card_key]: { count: r.total ?? r.count, name: r.view_name, ok: true } })))
              .catch(() => setViewTiles(prev => ({ ...prev, [c.card_key]: { count: 0, name: '', ok: false } })))
          api.getViewCounts()
            .then(r => {
              for (const c of tileCards) {
                const vid = (c as any).config.view.view_id as string
                const entry = r.counts?.[vid]
                if (entry && typeof entry.total === 'number' && (c as any).title)
                  setViewTiles(prev => ({ ...prev, [c.card_key]: { count: entry.total, name: '', ok: true } }))
                else void tileFallback(c, vid)
              }
            })
            .catch(() => { for (const c of tileCards) void tileFallback(c, (c as any).config.view.view_id as string) })
        }
      })
      .catch(() => { /* default layout */ })
    const interval = setInterval(() => refresh(true), 60_000)
    return () => clearInterval(interval)
    // BADGE-SAFE (P0 2026-08-06 postmortem): MOUNT-ONLY. This was keyed on [onScreenEnter],
    // and App recreates that closure on every render (`enter(s) => () => ...`), so any App
    // re-render (~every 20-35s via ambient polling) re-ran this WHOLE chain — summary, layout,
    // and a tile query per view — and reset the interval so the intended 60s cadence never
    // fired. Verified in the 2026-08-06 prod crawl: the chain ran 3× in one 70s dwell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return <div style={pageStyle}><LoadingState label="Loading your morning brief…" /></div>
  if (error) return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data) return null

  const { action_lane: action, volume_sparkline: spark, status_breakdown: statuses } = data
  // ASP-71 (re-opens ASP-50): every pulse tile deep-links to /rx?mf=<status filter>
  // — the EXACT filtered list for that PioneerRx status, pinned as a dismissible
  // chip (same mechanism as the custom metric cards, D-HOME-2). The old fixed-tab
  // routes were the bug Patrick reported: several statuses landed on the wrong tab
  // (Refills Remaining -> patient refill REQUESTS tab, hence an empty page), two
  // distinct statuses shared one destination (Fill in Progress AND Fill Requested
  // -> /rx/intake), and unmapped statuses fell to the bare unfiltered /rx list.
  const STATUS_META: Record<string, { icon: typeof Clock; tone: 'ok' | 'warn'; sub: string }> = {
    'Fill in Progress':     { icon: Clock,         tone: 'ok',   sub: 'Being filled now' },
    'Fill Requested':       { icon: Inbox,         tone: 'ok',   sub: 'Requested, not started' },
    'Refills Remaining':    { icon: RefreshCcw,    tone: 'ok',   sub: 'Filled · refills left' },
    'On Hold':              { icon: AlertTriangle, tone: 'warn', sub: 'Needs intervention' },
    'Discontinued':         { icon: XCircle,       tone: 'ok',   sub: 'Stopped by prescriber' },
    'Expired or No Refills':{ icon: ClipboardList, tone: 'ok',   sub: 'No refills left' },
    'Ready':                { icon: Pill,          tone: 'ok',   sub: 'Ready to fill' },
    'Out for Delivery':     { icon: Truck,         tone: 'ok',   sub: 'In transit' },
  }
  function statusMeta(status: string) {
    // Vendor-NEUTRAL fallback (D-PMS-1). This said 'PioneerRx status', which is
    // wrong on any tenant running something else — Bayview is on Liberty — and it
    // is the caption under every unmapped status tile on the home dashboard.
    return STATUS_META[status] ?? { icon: Activity, tone: 'ok' as const, sub: 'Dispensing system status' }
  }
  const pulseTileLink = (status: string, label: string) =>
    metricRxLink(label, { filters: [{ field: 'pioneerrx_rx_status', op: 'eq', values: [status] }] })

  // Friendly greeting
  const hour = new Date().getHours()
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const firstName = me?.display_name?.split(' ')[0] ?? ''

  // ASP-32 / D-HOME-1: section registry. Order/visibility come from the per-pharmacy
  // 'home' layout when one is saved; otherwise DEFAULT_HOME_ORDER below matches the
  // pre-existing hardcoded page exactly (all visible), so unconfigured pharmacies
  // (e.g. BioAccess) render pixel-identical. The action cards render inside one
  // "What needs you today" lane at the position of the first visible action card.
  const DEFAULT_HOME_ORDER = ['status_pulse', 'action_comments', 'action_prior_auth', 'action_holds', 'action_rejected', 'volume', 'go_deeper']
  const layoutOrder: string[] = homeLayout
    ? [...homeLayout].sort((a, b) => a.position - b.position).filter(c => c.visible).map(c => c.card_key)
    : DEFAULT_HOME_ORDER
  const layoutByKey = new Map((homeLayout ?? []).map(c => [c.card_key, c]))
  // D-HOME-3: section/card titles come from the saved layout when present, so a card
  // renamed (or auto-renamed by a parameter change, e.g. "Holds over 48h") in the
  // builder displays truthfully here. No saved layout => hardcoded defaults, unchanged.
  const layoutTitle = (key: string, fallback: string) => layoutByKey.get(key)?.title || fallback

  // ASP-54 (landing for real — the tracker previously claimed this shipped, but the
  // CTA still routed to bare /rx/intake): the aging card's "See all" mirrors the
  // server predicate exactly — state in (draft, submitted) AND created_at older than
  // the configured aging window — so "the 483 items" open as a visible filtered list.
  const agingDays: number = ((layoutByKey.get('action_aging')?.config as any)?.params?.aging_days) ?? 10
  const agingWorklistLink = () =>
    metricRxLink(layoutTitle('action_aging', `Aging — pre-check over ${agingDays} days`), {
      filters: [{ field: 'state', op: 'in', values: ['draft', 'submitted'] }],
      window: { field: 'created_at', mode: 'older_than', amount: agingDays, unit: 'days' },
    })

  // ASP-73 (complete home-card click-through audit, Patrick 2026-07-23): every count
  // on this page must open EXACTLY the rows it counted. Holds/PA/Rejected previously
  // routed to a whole tab or a dashboard (e.g. "10 holds over 24h" -> the full 2,022-row
  // On Hold tab). Each link below mirrors its server predicate verbatim
  // (DashboardFunctions: 3b/3c/3d), parameterized by the pharmacy's configured windows.
  const cardParams = action.card_params
  const holdHours: number = cardParams?.hold_hours ?? 24
  // NOTE: cardParams.rejected_hours is intentionally no longer read. The Rejected card is a
  // worklist of OPEN claim rejections, and an open rejection does not stop being open because
  // it aged past a window — Alpida's oldest still-rejected Rx has been outstanding since May.
  // The config field and its Home-builder editor are left intact pending Lucas/Maya's ruling on
  // whether to repurpose or retire it (see PR).
  const paStates: string[] = (cardParams?.pa_states?.length ? cardParams.pa_states : ['pa_required', 'pa_pending'])
  // TRUE holds count (server COUNT) — old_holds is a TOP-10 sample; using its length
  // capped the card at 10 regardless of the real number.
  const oldHoldsCount: number = action.old_holds_count ?? action.old_holds.length
  // ASP-74: per-pharmacy volume window (Home builder; server sizes the sparkline).
  const volumeDays: number = cardParams?.volume_days ?? 14
  const holdsWorklistLink = () =>
    metricRxLink(layoutTitle('action_holds', `Holds over ${holdHours}h`), {
      filters: [{ field: 'state', op: 'eq', values: ['on_hold'] }],
      window: { field: 'last_state_change_at', mode: 'older_than', amount: holdHours, unit: 'hours' },
    })
  const paWorklistLink = () =>
    metricRxLink(layoutTitle('action_prior_auth', 'Prior auth'), {
      filters: [{ field: 'payer_state', op: 'in', values: paStates }],
    })
  // Open claim rejections. NOT a metricRxLink: mf replays its predicate client-side over the
  // loaded rows, and the fields it can reach are rx_items columns only. A claim rejection lives
  // on rx_claims.transaction_response_status, so this routes to the server-side filter that
  // shares its predicate with the card's own COUNT (RejectedClaimPredicate).
  const rejectedWorklistLink = () => '/rx?claim_rejected=1'

  // D-HOME-2 part B: pulse tile display config (hide / relabel / order) from the saved
  // status_pulse card. Tiles stay data-driven; statuses not in `order` append after the
  // ordered ones so a new PioneerRx status never silently vanishes. No saved layout (or
  // no pulse config) => `statuses` passes through untouched.
  const pulseCfg = layoutByKey.get('status_pulse')?.config?.pulse
  const pulseStatuses = (() => {
    if (!pulseCfg) return statuses
    const hidden = new Set(pulseCfg.hidden ?? [])
    const order = pulseCfg.order ?? []
    const rank = new Map(order.map((s, i) => [s, i]))
    return statuses
      .filter(s => !hidden.has(s.status))
      .map(s => ({ ...s, display: pulseCfg.labels?.[s.status]?.trim() || s.status }))
      .sort((a, b) => (rank.get(a.status) ?? order.length) - (rank.get(b.status) ?? order.length))
  })()

  const actionKeys = layoutOrder.filter(k => k.startsWith('action_'))
  const visibleActionCount =
    (actionKeys.includes('action_comments') ? action.unread_provider_comments : 0) +
    (actionKeys.includes('action_prior_auth') ? action.pa_awaiting : 0) +
    (actionKeys.includes('action_holds') ? oldHoldsCount : 0) +
    (actionKeys.includes('action_rejected') ? action.rejected_24h : 0) +
    (actionKeys.includes('action_aging') ? (action.aging_precheck?.count ?? 0) : 0)

  // ASP-72: the headline count is clickable and answers "what ARE those N items?"
  // — an inline breakdown of every contributing bucket, each row deep-linking to
  // its exact worklist. Sums to visibleActionCount by construction (same terms).
  const attentionBreakdown = [
    { key: 'action_comments',   label: layoutTitle('action_comments', 'Provider comments'),   count: action.unread_provider_comments,     go: () => navigate('/notifications') },
    { key: 'action_prior_auth', label: layoutTitle('action_prior_auth', 'Prior auth'),        count: action.pa_awaiting,                  go: () => navigate(paWorklistLink()) },
    { key: 'action_holds',      label: layoutTitle('action_holds', `Holds over ${holdHours}h`), count: oldHoldsCount,                     go: () => navigate(holdsWorklistLink()) },
    { key: 'action_rejected',   label: layoutTitle('action_rejected', 'Rejected claims'), count: action.rejected_24h,       go: () => navigate(rejectedWorklistLink()) },
    { key: 'action_aging',      label: layoutTitle('action_aging', `Aging — pre-check over ${agingDays} days`), count: action.aging_precheck?.count ?? 0, go: () => navigate(agingWorklistLink()) },
  ].filter(b => actionKeys.includes(b.key) && b.count > 0)

  const actionCardRenderers: Record<string, () => React.ReactNode> = {
    action_comments: () => (
        <ActionCard
          key="action_comments"
          icon={MessageSquare}
          title={layoutTitle('action_comments', "Provider comments")}
          count={action.unread_provider_comments}
          ctaLabel="Open inbox"
          ctaOnClick={() => navigate('/notifications')}
        >
          {action.recent_comments.length === 0 ? (
            <EmptyMini message="No unread comments." />
          ) : (
            <ul style={listStyle}>
              {action.recent_comments.slice(0, 5).map(c => (
                <li key={c.id} style={listItemStyle}>
                  <Link to={`/rx/${c.rx_id}`} style={listLinkStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: 12 }}>{c.author_name}</strong>
                      <span style={mutedSm}>{relativeTime(c.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>
                      {c.drug_name} · {c.patient_name} <SpecialtyBadge flag={spFlags[c.patient_id]} />
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4, color: 'var(--color-text)' }} title={c.body}>
                      {truncate(c.body, 110)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ActionCard>
    ),
    action_prior_auth: () => (
        <ActionCard
          key="action_prior_auth"
          icon={FileCheck2}
          title={layoutTitle('action_prior_auth', "Prior auth")}
          count={action.pa_awaiting}
          ctaLabel="See all"
          ctaOnClick={() => navigate(paWorklistLink())}
        >
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)', padding: '8px 0' }}>
            {action.pa_awaiting === 0
              ? 'No prescriptions awaiting prior authorization.'
              : <><strong>{action.pa_awaiting}</strong> {action.pa_awaiting === 1 ? 'prescription is' : 'prescriptions are'} awaiting PA submission. Provider response is expected.</>}
          </div>
        </ActionCard>
    ),
    action_holds: () => (
        <ActionCard
          key="action_holds"
          icon={AlertTriangle}
          title={layoutTitle('action_holds', `Holds over ${holdHours}h`)}
          count={oldHoldsCount}
          ctaLabel="See all"
          ctaOnClick={() => navigate(holdsWorklistLink())}
        >
          {action.old_holds.length === 0 ? (
            <EmptyMini message="No long-running holds." />
          ) : (
            <ul style={listStyle}>
              {action.old_holds.slice(0, 5).map(h => (
                <li key={h.rx_id} style={listItemStyle}>
                  <Link to={`/rx/${h.rx_id}`} style={listLinkStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: 12 }}>{h.drug_name}</strong>
                      <span style={{ ...mutedSm, color: '#a16207', fontWeight: 500 }}>
                        {h.hours_in_hold}h
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>{h.patient_name} <SpecialtyBadge flag={spFlags[h.patient_id]} /></div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ActionCard>
    ),
    action_rejected: () => (
        <ActionCard
          key="action_rejected"
          icon={XCircle}
          title={layoutTitle('action_rejected', 'Rejected claims')}
          count={action.rejected_24h}
          ctaLabel="See all"
          ctaOnClick={() => navigate(rejectedWorklistLink())}
        >
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)', padding: '8px 0' }}>
            {action.rejected_24h === 0
              ? 'No open claim rejections.'
              : <>{action.rejected_24h} {action.rejected_24h === 1 ? 'prescription' : 'prescriptions'} whose most recent claim was rejected by the payer. Rebilled claims drop off this list automatically.</>}
          </div>
        </ActionCard>
    ),
    // ASP-07: opt-in aging card (not in DEFAULT_HOME_ORDER — a pharmacy enables it in
    // the Home dashboard builder). Rx unverified for over 10 days, oldest first.
    action_aging: () => (
        <ActionCard
          key="action_aging"
          icon={ClipboardList}
          title={layoutTitle('action_aging', 'Aging — pre-check over 10 days')}
          count={action.aging_precheck?.count ?? 0}
          ctaLabel="See all"
          ctaOnClick={() => navigate(agingWorklistLink())}
        >
          {(action.aging_precheck?.items?.length ?? 0) === 0 ? (
            <EmptyMini message="Nothing has been in pre-check over 10 days." />
          ) : (
            <ul style={listStyle}>
              {action.aging_precheck.items.slice(0, 5).map(a => (
                <li key={a.rx_id} style={listItemStyle}>
                  <Link to={`/rx/${a.rx_id}`} style={listLinkStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: 12 }}>{a.drug_name}</strong>
                      <span style={{ ...mutedSm, color: '#b91c1c', fontWeight: 600 }}>{a.days_in_precheck}d</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>{a.patient_name} <SpecialtyBadge flag={spFlags[a.patient_id]} /></div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ActionCard>
    ),
  }

  const sectionRenderers: Record<string, () => React.ReactNode> = {
    status_pulse: () => (
      <React.Fragment key="status_pulse">
      <h2 style={sectionLabelStyle}>Prescription status</h2>
      <div style={pulseGridStyle}>
        {pulseStatuses.map(s => {
          const meta = statusMeta(s.status)
          const label = (s as any).display ?? s.status
          return (
            <PulseCard
              key={s.status}
              icon={meta.icon}
              label={label}
              count={s.count}
              subtext={meta.sub}
              tone={meta.tone}
              onClick={() => navigate(pulseTileLink(s.status, label))}
            />
          )
        })}
      </div>
      </React.Fragment>
    ),
    action_lane: () => (
      <React.Fragment key="action_lane">
      <h2 style={sectionLabelStyle}>What needs you today</h2>
      <div style={actionGridStyle}>
        {actionKeys.map(k => actionCardRenderers[k]?.())}
      </div>
      </React.Fragment>
    ),
    volume: () => (
      <React.Fragment key="volume">
      <h2 style={sectionLabelStyle}>{layoutTitle('volume', `Volume — last ${volumeDays} days`)}</h2>
      <div style={cardStyle}>
        <div style={{ padding: 16, display: 'flex', gap: 24, alignItems: 'flex-end' }}>
          <Sparkline data={spark.map(d => d.count)} />
          <div style={{ minWidth: 200 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1 }}>
              {spark[spark.length - 1]?.count ?? 0}
            </div>
            <div style={mutedSm}>Rx today</div>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-soft)' }}>
              {volumeDays}-day average:{' '}
              <strong style={{ color: 'var(--color-text)' }}>
                {Math.round(spark.reduce((a, d) => a + d.count, 0) / Math.max(1, spark.length))}
              </strong>
              {' '}per day
            </div>
          </div>
        </div>
      </div>
      </React.Fragment>
    ),
    go_deeper: () => (
      <React.Fragment key="go_deeper">
      <h2 style={sectionLabelStyle}>Go deeper</h2>
      <div style={quickLinksGridStyle}>
        <QuickLink to="/dashboards/operations" icon={Inbox}
          title="Operational dashboard"
          description="Queue depth, time-in-state, hold reasons, throughput." />
        <QuickLink to="/dashboards/clinical" icon={AlertCircle}
          title="Clinical dashboard"
          description="High-alert drugs, polypharmacy, open clinical questions, PA aging." />
        <QuickLink to="/reports" icon={FileText}
          title="Reports"
          description="Volume, payer mix, controls, provider activity, refill performance." />
        <QuickLink to="/patients" icon={Pill}
          title="Patients"
          description="Search and review patient profiles." />
      </div>
      </React.Fragment>
    ),
  }

  // Walk the configured order; the action lane renders once, where the first visible
  // action card sits. kind='home_metric' cards (D-HOME-2 part B) render from the
  // resolved metric values; consecutive metric cards share one grid row. Other unknown
  // keys (future cards) are ignored.
  const rendered = new Set<string>()
  const body: React.ReactNode[] = []
  let metricRun: React.ReactNode[] = []
  const flushMetrics = () => {
    if (metricRun.length === 0) return
    body.push(<div key={`metrics_${body.length}`} style={actionGridStyle}>{metricRun}</div>)
    metricRun = []
  }
  for (const k of layoutOrder) {
    const card = layoutByKey.get(k)
    if (card?.kind === 'home_metric') {
      metricRun.push(<MetricCard key={k} title={card.title} metric={card.config?.metric} result={metricResults[k]} />)
      continue
    }
    // ASP-53: custom-view tiles share the metric grid row — a saved view's live
    // count, click-through to the view itself (/rx/view/:id shows exactly those rows).
    if (card?.kind === 'custom_view') {
      const vid = (card as any).config?.view?.view_id
      const tile = viewTiles[k]
      metricRun.push(
        <button
          key={k}
          onClick={() => vid && navigate(`/rx/view/${vid}`)}
          style={{ ...cardStyle, padding: 16, textAlign: 'left', cursor: vid ? 'pointer' : 'default', border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)', font: 'inherit' }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClipboardList size={13} /> {card.title || tile?.name || 'Saved view'}
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-text)', margin: '6px 0 2px' }}>
            {tile ? (tile.ok ? tile.count : '—') : '…'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
            {tile && !tile.ok ? 'Count unavailable' : 'Saved view · open worklist →'}
          </div>
        </button>
      )
      continue
    }
    flushMetrics()
    if (k.startsWith('action_')) {
      if (!rendered.has('action_lane') && actionKeys.length > 0) { rendered.add('action_lane'); body.push(sectionRenderers.action_lane()) }
      continue
    }
    if (sectionRenderers[k] && !rendered.has(k)) { rendered.add(k); body.push(sectionRenderers[k]()) }
  }
  flushMetrics()

  return (
    <div style={pageStyle}>
      {/* Greeting + summary */}
      <header style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={titleStyle}>{greet}{firstName ? `, ${firstName}` : ''}.</h1>
          <p style={subtitleStyle}>
            {visibleActionCount === 0
              ? "You're caught up. The bench is moving."
              : <>
                  <button
                    onClick={() => setShowAttention(v => !v)}
                    title="See what makes up this count"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}
                  >
                    <strong>{visibleActionCount}</strong> {visibleActionCount === 1 ? 'item needs' : 'items need'} attention today
                  </button>.
                </>}
          </p>
          {showAttention && attentionBreakdown.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface, #fff)', padding: '4px 0', maxWidth: 420 }}>
              {attentionBreakdown.map(b => (
                <button
                  key={b.key}
                  onClick={b.go}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', background: 'none', border: 'none', padding: '8px 14px', cursor: 'pointer', font: 'inherit', color: 'var(--color-text)', textAlign: 'left' }}
                >
                  <span style={{ fontSize: 13 }}>{b.label}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                    <strong style={{ fontSize: 13 }}>{b.count}</strong>
                    <span style={{ fontSize: 12, color: 'var(--color-primary, #1a7f5a)' }}>View →</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-mute)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#27ae60', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            Live · updated {Math.round((Date.now() - refreshedAt.getTime()) / 1000)}s ago
          </span>
          <button onClick={() => refresh()} title="Refresh now"
            style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--color-text)' }}>
            <RefreshCcw size={10} /> Refresh
          </button>
        </div>
      </header>

      {body}
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function PulseCard({ icon: Icon, label, count, subtext, tone, onClick }: {
  icon: any
  label: string
  count: number
  subtext: string
  tone: 'ok' | 'warn'
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...pulseCardStyle,
        borderLeftColor: tone === 'warn' ? '#f59e0b' : 'var(--color-border)',
        borderLeftWidth: tone === 'warn' ? 3 : 1,
      }}
      aria-label={`${label}: ${count}. ${subtext}.`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-soft)' }}>
        <Icon size={14} aria-hidden="true" />
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.1, marginTop: 8, color: 'var(--color-text)' }}>
        {count}
      </div>
      <div style={{ ...mutedSm, marginTop: 4 }}>{subtext}</div>
      <ArrowRight size={14} style={{ position: 'absolute', top: 14, right: 14, color: 'var(--color-text-mute)' }} aria-hidden="true" />
    </button>
  )
}

function ActionCard({ icon: Icon, title, count, ctaLabel, ctaOnClick, children }: {
  icon: any
  title: string
  count: number
  ctaLabel: string
  ctaOnClick: () => void
  children: React.ReactNode
}) {
  return (
    <section style={cardStyle}>
      <header style={cardHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={14} aria-hidden="true" />
          <h3 style={cardTitleStyle}>{title}</h3>
          {count > 0 && <span style={countBadgeStyle}>{count}</span>}
        </div>
        <button onClick={ctaOnClick} style={ctaBtnStyle}>{ctaLabel} →</button>
      </header>
      <div style={{ padding: '0 14px 14px' }}>{children}</div>
    </section>
  )
}

function QuickLink({ to, icon: Icon, title, description }: {
  to: string
  icon: any
  title: string
  description: string
}) {
  return (
    <Link to={to} style={quickLinkStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'var(--color-primary, #0d9488)' }}>
        <Icon size={16} aria-hidden="true" />
        <strong style={{ fontSize: 14 }}>{title}</strong>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{description}</div>
    </Link>
  )
}

function Sparkline({ data, height = 56, width = 320 }: { data: number[]; height?: number; width?: number }) {
  if (data.length === 0) return <div style={{ height, width, color: 'var(--color-text-mute)' }}>No data</div>
  const max = Math.max(...data, 1)
  const stepX = width / Math.max(1, data.length - 1)
  const points = data.map((v, i) => `${i * stepX},${height - (v / max) * height}`).join(' ')
  // Area fill via polygon
  const areaPoints = `0,${height} ${points} ${width},${height}`
  return (
    <svg width={width} height={height} role="img" aria-label="14-day Rx volume sparkline" style={{ display: 'block' }}>
      <polygon points={areaPoints} fill="rgba(13,148,136,0.12)" />
      <polyline points={points} fill="none" stroke="var(--color-primary, #0d9488)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {data.map((v, i) => (
        <circle key={i} cx={i * stepX} cy={height - (v / max) * height} r={2.5} fill="var(--color-primary, #0d9488)" />
      ))}
    </svg>
  )
}

// D-HOME-2 part B — a configurable metric card. Number display, or a tile grid when
// the metric is grouped. `result` undefined = still resolving / endpoint failed.
// Click-through (B2): the card is a worklist entry point — clicking navigates to the
// prescription list with the card's exact filters pre-applied (pinned chip); a group
// tile additionally pins its group value. rx_items-sourced cards only.
function MetricCard({ title, metric, result }: { title: string; metric?: api.RxCardConfig['metric']; result?: api.HomeMetricResult }) {
  const navigate = useNavigate()
  const fmt = (v: number | null | undefined) =>
    v == null ? '—' : Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 })
  const linkable = !!metric && (metric.source ?? 'rx_items') === 'rx_items' && !result?.error
  const go = (group?: string) => {
    if (!linkable || !metric) return
    navigate(metricRxLink(title, metric as any, group !== undefined && metric.group_by
      ? { group_by: metric.group_by, group } : undefined))
  }
  return (
    <section
      style={{ ...cardStyle, cursor: linkable && !result?.groups ? 'pointer' : 'default' }}
      role={linkable && !result?.groups ? 'button' : undefined}
      tabIndex={linkable && !result?.groups ? 0 : undefined}
      aria-label={linkable && !result?.groups ? `${title} — open matching prescriptions` : undefined}
      onClick={() => { if (!result?.groups) go() }}
      onKeyDown={e => { if (!result?.groups && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); go() } }}
    >
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-soft)' }}>{title}</div>
        {!result ? (
          <div style={{ ...mutedSm, padding: '10px 0' }}>Metric unavailable.</div>
        ) : result.error ? (
          <div style={{ ...mutedSm, padding: '10px 0' }}>This metric's configuration is invalid — fix it in the Home dashboard builder.</div>
        ) : result.groups ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 10 }}>
            {result.groups.map(g => (
              <div key={g.group} role={linkable ? 'button' : undefined} tabIndex={linkable ? 0 : undefined}
                aria-label={linkable ? `${title} — ${g.group}: open matching prescriptions` : undefined}
                onClick={e => { e.stopPropagation(); go(g.group) }}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); go(g.group) } }}
                style={{ minWidth: 96, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, cursor: linkable ? 'pointer' : 'default' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.1 }}>{fmt(g.value)}</div>
                <div style={{ ...mutedSm, marginTop: 2 }}>{g.group}</div>
              </div>
            ))}
            {result.groups.length === 0 && <div style={mutedSm}>No matching data.</div>}
          </div>
        ) : (
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.2, paddingTop: 6 }}>
            {fmt(result.value)}
            {result.agg && result.agg !== 'count' && <span style={{ ...mutedSm, marginLeft: 8, fontWeight: 500 }}>{result.agg.toUpperCase()}</span>}
          </div>
        )}
      </div>
    </section>
  )
}

function EmptyMini({ message }: { message: string }) {
  return <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--color-text-mute)' }}>{message}</div>
}

function truncate(s: string, n: number) {
  return s.length <= n ? s : s.slice(0, n - 1) + '…'
}

// ============================================================================
// Styles
// ============================================================================

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 32, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 6, color: 'var(--color-text-soft)', fontSize: 14 }
const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
  color: 'var(--color-text-mute)', margin: '24px 0 10px',
}
const pulseGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12,
}
const pulseCardStyle: React.CSSProperties = {
  position: 'relative',
  textAlign: 'left',
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 16,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s, box-shadow 0.15s',
}
const actionGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12,
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const cardHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 14px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-alt)', color: 'var(--color-text-soft)',
}
const cardTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
}
const countBadgeStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: 11, fontWeight: 700,
  background: 'var(--color-text)', color: 'var(--color-surface, #fff)',
  padding: '1px 7px', borderRadius: 10,
}
const ctaBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-primary, #0d9488)', cursor: 'pointer', fontFamily: 'inherit',
}
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const listItemStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)',
}
const listLinkStyle: React.CSSProperties = {
  display: 'block', padding: '10px 0', textDecoration: 'none', color: 'inherit',
}
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const quickLinksGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10,
}
const quickLinkStyle: React.CSSProperties = {
  display: 'block', padding: 14,
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, textDecoration: 'none', color: 'inherit',
}
